// Typed wrappers for the /v1/merchants surface.
//
// BFF rule (CLAUDE.md rule 1): all calls go through apiClient — the single
// axios instance that injects Authorization: Bearer <token> (in-memory) and
// X-Merchant-ID (localStorage-mirrored). Never construct a new axios instance.
import { apiClient } from "./client";
import type { PublicBankProvider } from "./bankAccounts";

/**
 * Merchant — the merchant record as returned by payouts-backend.
 *
 * Fields mirror the `merchants` table 1:1 (id, slug, display_name, status,
 * timestamps). The backend has no industry / country / owner / MRR / balance
 * columns — those were mock-only fields and are intentionally absent here.
 * `status` is one of: active | suspended | closed.
 */
/**
 * BalanceDisplay — per-merchant superadmin lever (backend migration 030,
 * payouts-backend issue #2) controlling what `GET /v1/merchants/:id/paysmart-balance`
 * returns.
 *
 *   "live_paysmart" (default) — live IDFC GetMerchantBalance call.
 *   "hidden"                  — short-circuit; never reach upstream.
 *   "ledger"                  — placeholder zero until the ledger source lands.
 *
 * The CHECK constraint at the DB layer pins this exact set.
 */
export type BalanceDisplay = "live_paysmart" | "hidden" | "ledger";

export interface Merchant {
  id: number;
  slug: string;
  display_name: string;
  status: string;
  created_at: string;
  updated_at: string;
  disabled_at: string | null;
  // Per-merchant superadmin API toggle (backend migration 027). Gates
  // both stk_… key auth (BearerDispatch returns 401 api_disabled when
  // false) and the panel's API keys section. NEW merchants default to
  // false; existing merchants were backfilled to true.
  api_enabled?: boolean;
  // Per-merchant balance-display lever (backend migration 030). Optional
  // on the wire so legacy responses without the field don't break the
  // type — treat undefined as "live_paysmart" at the call site.
  balance_display?: BalanceDisplay;
  // (Migration 034 moved auto_approve off merchants onto bank_accounts.
  // The per-rail flag is read/written through src/api/bankAccounts.ts.)

  /** Embedded bank rails — present on GET /v1/merchants/:id responses
   *  (the M2M public shape strips them too but the JWT panel shape keeps
   *  them). Optional because the listMerchants response doesn't carry the
   *  embed. */
  bank_accounts?: BankAccountItem[];
}

/** BankAccountItem — the trimmed bank rail shape embedded in
 *  GET /v1/merchants/:id. Mirrors the backend's bankAccountItem DTO.
 *  `provider` is the neutralised public token ("idfc" | "buckzy" |
 *  "razorpayx" — see PublicBankProvider in src/api/bankAccounts.ts). */
export interface BankAccountItem {
  id: number;
  merchant_id: number;
  label: string;
  provider: PublicBankProvider;
  currency: string;
  status: "active" | "pending" | "disabled";
  created_at: string;
  disabled_at: string | null;
  /** The operator's off switch (backend migration 050). See BankAccount in
   *  src/api/bankAccounts.ts for why this is distinct from `disabled_at`. */
  disabled?: boolean;
  disabled_reason?: string | null;
}

/**
 * listMerchants — GET /v1/merchants
 * Admin-only: returns every merchant, ordered by id ascending.
 */
export async function listMerchants(): Promise<Merchant[]> {
  const resp = await apiClient.get<{ merchants: Merchant[] }>("/v1/merchants");
  return resp.data.merchants;
}

/**
 * getMerchant — GET /v1/merchants/:idOrSlug
 *
 * Accepts either the integer merchant id (legacy) or the slug (recommended;
 * panel URL bar uses this form). Backend's `RequireMerchantScope` resolver
 * (payouts-backend#8) handles either format. Requires admin role or a
 * membership on the resolved merchant.
 *
 * Response embeds the bank rails (payouts-backend#5), so a fresh page-load
 * gets both the merchant and the rails in one round trip.
 */
export async function getMerchant(
  idOrSlug: number | string,
): Promise<Merchant> {
  const resp = await apiClient.get<Merchant>(`/v1/merchants/${idOrSlug}`);
  return resp.data;
}

/**
 * CreateMerchantPayload — POST /v1/merchants body. `slug` is the permanent
 * identifier (lowercase letters, digits, hyphens — 2–40 chars).
 */
export interface CreateMerchantPayload {
  slug: string;
  display_name: string;
}

/**
 * createMerchant — POST /v1/merchants
 * Admin-only. Provisions a new merchant (status "active"); returns the row.
 */
export async function createMerchant(
  payload: CreateMerchantPayload,
): Promise<Merchant> {
  const resp = await apiClient.post<Merchant>("/v1/merchants", payload);
  return resp.data;
}

/**
 * UpdateMerchantPayload — PATCH /v1/merchants/:id body. Both fields optional
 * (at least one required). `slug` is the immutable identifier — not editable.
 * `status` must be one of: active | suspended | closed.
 */
export interface UpdateMerchantPayload {
  display_name?: string;
  status?: string;
  api_enabled?: boolean;
  /**
   * Per-merchant balance-display lever (backend migration 030). Sending a
   * value outside the BalanceDisplay union 400s with
   * code=balance_display_invalid; sending undefined leaves the column alone.
   */
  balance_display?: BalanceDisplay;
}

/**
 * updateMerchant — PATCH /v1/merchants/:id
 * Admin-only. Edits display_name and/or status; returns the updated merchant.
 */
export async function updateMerchant(
  id: number,
  payload: UpdateMerchantPayload,
): Promise<Merchant> {
  const resp = await apiClient.patch<Merchant>(`/v1/merchants/${id}`, payload);
  return resp.data;
}

/**
 * DeleteMerchantResult — DELETE /v1/merchants/:id response shape.
 *
 * Reality of multi-tenant ops: every dependent table (bank_accounts,
 * payouts, beneficiaries, api_keys, …) references `merchants(id)` with
 * `ON DELETE RESTRICT`. The moment a merchant has any history it CANNOT
 * be hard-deleted — that history is the compliance trail for funds the
 * platform has already moved on their behalf.
 *
 * The backend offers two outcomes from a single endpoint:
 *   - `deleted: true,  closed: false` — hard-deleted (no dependent rows)
 *   - `deleted: false, closed: true`  — soft-closed (status='closed' +
 *                                       disabled_at=now()); row remains
 *                                       for audit + history
 *
 * The panel renders the appropriate copy from these flags.
 */
export interface DeleteMerchantResult {
  deleted: boolean;
  closed: boolean;
}

/**
 * deleteMerchant — DELETE /v1/merchants/:id
 * Admin-only. Smart fallback: hard-deletes if the merchant is clean
 * (just-created mistake, never used), otherwise soft-closes. The response
 * tells the caller which path was taken.
 */
export async function deleteMerchant(
  id: number,
): Promise<DeleteMerchantResult> {
  const resp = await apiClient.delete<DeleteMerchantResult>(
    `/v1/merchants/${id}`,
  );
  return resp.data;
}
